import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { RetriveImportacaoService } from 'src/services/retrive-importacao.service';
import { RetriveService } from 'src/services/retrive.service';
import { EstacaoService } from 'src/services/estacao.service';
import { TipoVariavelService } from 'src/services/tipo-variavel.service';
import { PermissoesService } from 'src/services/permissoes.service';
import * as moment from 'moment';
// XLSX será importado dinamicamente
import * as FileSaver from 'file-saver';
import * as echarts from 'echarts';

@Component({
    selector: 'app-retrive-importacao',
    templateUrl: './retrive-importacao.component.html',
    styleUrls: ['./retrive-importacao.component.scss'],
    standalone: false
})
export class RetriveImportacaoComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('fileUpload') fileUpload: FileUpload;
  @ViewChild('chartContainer') chartContainer: ElementRef;
  @ViewChild('tableWrapper', { static: false }) tableWrapper: ElementRef;
  scrollHeight: string = '600px';

  // Dados principais
  data: any[] = [];
  loading: boolean = false;
  processandoArquivo: boolean = false;
  
  // Upload e processamento
  arquivoSelecionado: File | null = null;
  estacaoSelecionada: number | null = null;
  dadosProcessados: any[] = [];
  previewData: any[] = [];
  colunasArquivo: string[] = [];
  mapeamentoColunas: { [key: string]: number | null } = {}; // coluna_arquivo => tipo_variavel_id
  retrivesExistentes: Map<string, any> = new Map(); // Chave: "estacao_id|tipo_variavel_id|data_hora_dado", Valor: { id, valor }
  
  // Wizard de mapeamento
  mostrarWizard: boolean = false;
  passoWizard: number = 1; // 1: upload, 2: mapeamento, 3: preview, 4: importar
  colunasValor: string[] = []; // Colunas que contêm valores (não data/hora)
  
  // Estação e intervalo
  intervaloEstacao: number | null = null;
  dropDownEstacoes: any[] = [];
  dropDownTipoVariavel: any[] = [];
  
  // Modal de gráfico
  modalGrafico: boolean = false;
  graficoVariavelSelecionada: number | null = null;
  dadosComparativos: any = null;
  chartInstance: echarts.ECharts | null = null;
  private readonly onWindowResize = () => this.chartInstance?.resize();
  
  // Modal de análise por período
  modalAnalisePeriodo: boolean = false;
  estacaoAnalise: number | null = null;
  periodoAnalise: Date[] = [];
  intervaloOperacaoAnalise: number | null = null;
  
  // Usuário
  user: any = {};

  constructor(
    private retriveImportacaoService: RetriveImportacaoService,
    private retriveService: RetriveService,
    private estacaoService: EstacaoService,
    private tipoVariavelService: TipoVariavelService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    protected permissaoService: PermissoesService,
  ) {
    this.user = JSON.parse(localStorage.getItem('user') ?? '{}');
  }

  ngOnInit(): void {
    this.carregarEstacoes();
    this.carregarTiposVariavel();
    this.index();
  }

  ngOnDestroy(): void {
    this.descartarGrafico();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.calculateScrollHeight();
    }, 200);
  }

  calculateScrollHeight(): void {
    if (this.tableWrapper && this.tableWrapper.nativeElement) {
      const wrapperHeight = this.tableWrapper.nativeElement.offsetHeight;
      const calculatedHeight = wrapperHeight - 120;
      this.scrollHeight = `${Math.max(calculatedHeight, 300)}px`;
    }
  }

  carregarEstacoes(): void {
    this.estacaoService.lookup().subscribe({
      next: (res) => {
        const estacoes = res || [];
        this.dropDownEstacoes = estacoes.map((item: any) => ({
          label: item.descricao || `Estação ${item.id}`,
          value: item.id
        }));
      },
      error: (err) => {
        console.error('Erro ao carregar estações:', err);
      }
    });
  }

  carregarTiposVariavel(): void {
    this.tipoVariavelService.index().subscribe({
      next: (res) => {
        const tipos = res.data || res || [];
        this.dropDownTipoVariavel = tipos.map((item: any) => ({
          label: item.tipo_variavel || `Tipo ${item.id}`,
          value: item.id
        }));
      },
      error: (err) => {
        console.error('Erro ao carregar tipos de variável:', err);
      }
    });
  }

  index(): void {
    this.loading = true;
    this.retriveImportacaoService.listar({}).subscribe({
      next: (res) => {
        this.data = res.data || res || [];
        this.loading = false;
        setTimeout(() => {
          this.calculateScrollHeight();
        }, 200);
      },
      error: (err) => {
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Erro', 
          detail: 'Erro ao carregar importações' 
        });
        this.loading = false;
      }
    });
  }

  onFileSelect(event: any): void {
    const file = event.currentFiles?.[0];
    if (file) {
      const extensao = file.name.split('.').pop()?.toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(extensao || '')) {
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Erro', 
          detail: 'Formato de arquivo não suportado. Use CSV, XLSX ou XLS.' 
        });
        this.fileUpload?.clear();
        this.onFileRemove();
        return;
      }
      this.arquivoSelecionado = file;
      this.processarArquivo(file);
    }
  }

  onFileRemove(): void {
    this.arquivoSelecionado = null;
    this.colunasArquivo = [];
    this.colunasValor = [];
    this.dadosProcessados = [];
    this.mapeamentoColunas = {};
  }

  async processarArquivo(file: File): Promise<void> {
    this.processandoArquivo = true;
    try {
      const extensao = file.name.split('.').pop()?.toLowerCase();
      
      if (extensao === 'csv') {
        await this.processarCSV(file);
      } else if (extensao === 'xlsx' || extensao === 'xls') {
        await this.processarExcel(file);
      }
      
      this.identificarColunas();
    } catch (error) {
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Erro', 
        detail: 'Erro ao processar arquivo: ' + (error as Error).message 
      });
    } finally {
      this.processandoArquivo = false;
    }
  }

  async processarCSV(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const text = e.target.result;
          const lines = text.split(/\r?\n/).filter((line: string) => line.trim());
          
          // Detectar delimitador considerando CSVs com ponto e vírgula (comum em PT-BR)
          const detectarDelimitador = (linha: string): string => {
            const virgulas = (linha.match(/,/g) || []).length;
            const pontoEVirgula = (linha.match(/;/g) || []).length;
            return pontoEVirgula > virgulas ? ';' : ',';
          };
          
          if (lines.length < 2) {
            reject(new Error('Arquivo CSV deve ter pelo menos um cabeçalho e uma linha de dados'));
            return;
          }

          const delimitador = detectarDelimitador(lines[0]);
          
          // Separar linha respeitando aspas para não quebrar valores com delimitador interno
          const parseLinha = (linha: string): string[] => {
            const valores: string[] = [];
            let atual = '';
            let dentroDeAspas = false;
            
            for (let i = 0; i < linha.length; i++) {
              const char = linha[i];
              
              if (char === '"') {
                // Alternar estado, considerando aspas escapadas com duas aspas seguidas
                if (dentroDeAspas && linha[i + 1] === '"') {
                  atual += '"';
                  i++; // Pular a aspa escapada
                } else {
                  dentroDeAspas = !dentroDeAspas;
                }
                continue;
              }
              
              if (char === delimitador && !dentroDeAspas) {
                valores.push(atual.trim());
                atual = '';
              } else {
                atual += char;
              }
            }
            
            valores.push(atual.trim());
            
            return valores.map(v => v.replace(/^"(.*)"$/, '$1'));
          };
          
          const headers = parseLinha(lines[0]).map((h: string, index: number) => {
            const headerLimpo = h.replace(/\uFEFF/g, '').trim(); // Remover BOM se existir
            return headerLimpo || `Coluna_${index + 1}`;
          });
          this.colunasArquivo = headers;
          
          this.dadosProcessados = lines.slice(1)
            .map((line: string, index: number) => {
              const values = parseLinha(line);
              const obj: any = {};
              headers.forEach((header, i) => {
                obj[header] = values[i] !== undefined ? values[i] : '';
              });
              obj._linha = index + 2; // Linha no arquivo (começa em 2 porque linha 1 é cabeçalho)
              return obj;
            })
            // Remover linhas totalmente vazias
            .filter((linha: any) => {
              return Object.keys(linha).some(key => key !== '_linha' && String(linha[key]).trim() !== '');
            });
          
          console.log('CSV processado:', {
            delimitador,
            totalLinhas: this.dadosProcessados.length,
            colunas: this.colunasArquivo,
            primeiraLinha: this.dadosProcessados[0]
          });
          
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Erro ao ler arquivo CSV'));
      reader.readAsText(file);
    });
  }

  async processarExcel(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      import('xlsx').then(XLSX => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { 
              type: 'array', 
              cellDates: false,  // NÃO converter datas automaticamente
              cellNF: false 
            });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // Função para obter valor formatado da célula (como aparece no Excel)
            const obterValorFormatado = (cell: any): string => {
              if (!cell) return '';
              
              const ehDataOuHora = (): boolean => {
                if (cell.t === 'd') return true;
                const formato = cell.z || '';
                try {
                  if (formato && XLSX.SSF && typeof XLSX.SSF.is_date === 'function' && XLSX.SSF.is_date(formato)) {
                    return true;
                  }
                } catch (_) {}
                if (cell.t === 'n' && cell.w && /^(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})(\s+\d{1,2}:\d{2}(:\d{2})?)?$/.test(cell.w)) {
                  return true;
                }
                return false;
              };

              const formatarDataOuHora = (): string => {
                // Se o XLSX já trouxe um Date
                if (cell.t === 'd' && cell.v instanceof Date) {
                  const temHora = (cell.z || '').toLowerCase().includes('h');
                  return moment(cell.v).format(temHora ? 'DD/MM/YYYY HH:mm:ss' : 'DD/MM/YYYY');
                }
                
                // Se for número (serial do Excel)
                if (typeof cell.v === 'number') {
                  const parsed = XLSX.SSF.parse_date_code(cell.v);
                  if (parsed) {
                    const temHora = parsed.H !== 0 || parsed.M !== 0 || parsed.S !== 0;
                    const isTimeOnly = cell.v < 1 && !((cell.z || '').toLowerCase().includes('d') || (cell.z || '').toLowerCase().includes('y'));
                    
                    if (isTimeOnly) {
                      const hora = String(parsed.H || 0).padStart(2, '0');
                      const minuto = String(parsed.M || 0).padStart(2, '0');
                      const segundo = String(Math.floor(parsed.S || 0)).padStart(2, '0');
                      return `${hora}:${minuto}:${segundo}`;
                    }
                    
                    const data = moment({
                      year: parsed.y,
                      month: (parsed.m || 1) - 1,
                      date: parsed.d || 1,
                      hour: parsed.H || 0,
                      minute: parsed.M || 0,
                      second: Math.floor(parsed.S || 0)
                    });
                    
                    return data.format(temHora ? 'DD/MM/YYYY HH:mm:ss' : 'DD/MM/YYYY');
                  }
                }
                
                // Fallback para o valor formatado
                if (cell.w) return cell.w;
                if (cell.v !== undefined && cell.v !== null) return String(cell.v);
                return '';
              };
              
              if (ehDataOuHora()) {
                return formatarDataOuHora();
              }
              
              // Se a célula tem valor formatado (w), usar ele
              if (cell.w) {
                return cell.w;
              }
              
              // Caso contrário, converter o valor para string
              if (cell.v !== undefined && cell.v !== null) {
                return String(cell.v);
              }
              
              return '';
            };
            
            // Obter range da planilha
            const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
            
            // Ler dados linha por linha preservando formato original
            const jsonData: any[][] = [];
            for (let R = range.s.r; R <= range.e.r; R++) {
              const row: any[] = [];
              for (let C = range.s.c; C <= range.e.c; C++) {
                const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                const cell = worksheet[cellAddress];
                row.push(obterValorFormatado(cell));
              }
              jsonData.push(row);
            }
          
          if (jsonData.length < 2) {
            reject(new Error('Arquivo Excel deve ter pelo menos um cabeçalho e uma linha de dados'));
            return;
          }
          
          // Processar cabeçalhos - garantir que não sejam vazios
          const headers = (jsonData[0] as any[]).map((h: any, index: number) => {
            const header = String(h || '').trim();
            // Se o cabeçalho estiver vazio, usar um nome padrão
            return header || `Coluna_${index + 1}`;
          });
          this.colunasArquivo = headers;
          
          // Processar dados - filtrar linhas completamente vazias
          // jsonData[0] é o cabeçalho, então processar a partir do índice 1
          this.dadosProcessados = jsonData.slice(1)
            .filter((row: any[]) => {
              // Verificar se a linha tem pelo menos um valor não vazio
              return row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '');
            })
            .map((row: any[], index: number) => {
              const obj: any = {};
              headers.forEach((header, i) => {
                const valor = row[i];
                // Todos os valores já vêm como string formatada (preservando formato do Excel)
                if (valor !== undefined && valor !== null && String(valor).trim() !== '') {
                  obj[header] = String(valor).trim();
                } else {
                  obj[header] = '';
                }
              });
              obj._linha = index + 2; // +2 porque linha 1 é cabeçalho e index começa em 0
              return obj;
            });
          
          console.log('Excel processado:', {
            totalLinhas: jsonData.length,
            linhasProcessadas: this.dadosProcessados.length,
            colunas: this.colunasArquivo,
            primeiraLinha: this.dadosProcessados[0],
            todasColunas: headers
          });
          
            resolve();
          } catch (error) {
            console.error('Erro ao processar Excel:', error);
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error('Erro ao ler arquivo Excel'));
        reader.readAsArrayBuffer(file);
      }).catch(reject);
    });
  }

  identificarColunas(): void {
    // Identificar colunas de data/hora (normalizar nomes)
    const normalizarNome = (nome: string): string => {
      return nome.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');
    };
    
    const colunasDataHora: string[] = [];
    const colunasValor: string[] = [];
    
    this.colunasArquivo.forEach(col => {
      const colNormalizada = normalizarNome(col);
      // Verificar se é coluna de data ou hora
      if (colNormalizada.includes('data') || 
          colNormalizada.includes('hora') || 
          colNormalizada.includes('date') || 
          colNormalizada.includes('time') ||
          colNormalizada.includes('hour')) {
        colunasDataHora.push(col);
      } else {
        // Assumir que outras colunas são valores (ex: "valor nivel", "valor bateria")
        colunasValor.push(col);
      }
    });
    
    this.colunasValor = colunasValor;
    
    console.log('Colunas identificadas:', {
      todas: this.colunasArquivo,
      dataHora: colunasDataHora,
      valores: this.colunasValor,
      mapeamentoInicial: this.mapeamentoColunas
    });
    
    // Limpar mapeamento anterior
    this.mapeamentoColunas = {};
    
    // Tentar identificar automaticamente tipos de variável pelo nome da coluna
    this.colunasValor.forEach(col => {
      const colNormalizada = normalizarNome(col);
      const tipoEncontrado = this.dropDownTipoVariavel.find(tipo => {
        const tipoNormalizado = normalizarNome(tipo.label);
        // Verificar se o nome da coluna contém o tipo ou vice-versa
        return colNormalizada.includes(tipoNormalizado) || 
               tipoNormalizado.includes(colNormalizada) ||
               // Verificar palavras-chave comuns
               (colNormalizada.includes('nivel') && tipoNormalizado.includes('nivel')) ||
               (colNormalizada.includes('bateria') && tipoNormalizado.includes('bateria'));
      });
      
      if (tipoEncontrado) {
        this.mapeamentoColunas[col] = tipoEncontrado.value;
        console.log(`Mapeamento automático: "${col}" -> ${tipoEncontrado.label}`);
      } else {
        this.mapeamentoColunas[col] = null;
      }
    });
    
    console.log('Mapeamento final:', this.mapeamentoColunas);
  }

  getOpcoesTipoVariavel(colunaAtual: string) {
    const selecionadoAtual = this.mapeamentoColunas[colunaAtual];
    const selecionados = new Set(
      Object.entries(this.mapeamentoColunas)
        .filter(([col, tipo]) => col !== colunaAtual && !!tipo)
        .map(([, tipo]) => tipo as number)
    );

    return this.dropDownTipoVariavel.map(opt => ({
      ...opt,
      disabled: selecionados.has(opt.value) && opt.value !== selecionadoAtual
    }));
  }

  mapeamentoValido(): boolean {
    if (this.colunasValor.length === 0) return false;
    const possuiNaoMapeado = this.colunasValor.some(col => !this.mapeamentoColunas[col]);
    if (possuiNaoMapeado) return false;

    const selecionados = this.colunasValor
      .map(col => this.mapeamentoColunas[col])
      .filter((v): v is number => !!v);
    const temDuplicata = new Set(selecionados).size !== selecionados.length;
    return !temDuplicata;
  }

  private corpoConsulta(res: any): any {
    if (res && (Array.isArray(res.retrives) || Array.isArray(res.dados_variavel))) {
      return res;
    }

    return res?.data || res || {};
  }

  private ajustarHorarioBrasilia(dataHora: moment.Moment): moment.Moment {
    // Garante que a data/hora seja interpretada em BRT independente do fuso do dispositivo
    return dataHora.clone().utcOffset(-180, true);
  }

  irParaMapeamento(): void {
    if (!this.estacaoSelecionada) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione uma estação antes de continuar'
      });
      return;
    }

    if (!this.arquivoSelecionado) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione um arquivo para importar'
      });
      return;
    }

    if (this.processandoArquivo) {
      this.messageService.add({
        severity: 'info',
        summary: 'Processando',
        detail: 'Aguarde o processamento do arquivo finalizar'
      });
      return;
    }

    if (!this.dadosProcessados.length || !this.colunasArquivo.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Arquivo ainda não processado. Selecione novamente para processar.'
      });
      return;
    }

    this.passoWizard = 2;
  }

  obterIntervaloEstacao(callback?: () => void): void {
    if (!this.estacaoSelecionada) {
      if (callback) callback();
      return;
    }
    
    this.estacaoService.show(this.estacaoSelecionada).subscribe({
      next: (res) => {
        const estacao = res.estacao || res;
        this.intervaloEstacao = estacao.intervalo_operacao || null;
        
        if (!this.intervaloEstacao) {
          this.messageService.add({ 
            severity: 'warn', 
            summary: 'Atenção', 
            detail: 'Estação não possui intervalo configurado. Dados faltantes não serão preenchidos automaticamente.' 
          });
        }
        
        if (callback) callback();
      },
      error: (err) => {
        console.error('Erro ao obter intervalo da estação:', err);
        if (callback) callback();
      }
    });
  }

  gerarPreview(): void {
    if (!this.estacaoSelecionada) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: 'Selecione uma estação' 
      });
      return;
    }

    // Verificar se todos os valores estão mapeados
    const colunasSemMapeamento = this.colunasValor.filter(col => !this.mapeamentoColunas[col]);
    if (colunasSemMapeamento.length > 0) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: `Mapeie todas as colunas de valor. Colunas sem mapeamento: ${colunasSemMapeamento.join(', ')}` 
      });
      return;
    }

    // Verificar duplicidade de tipos de variável
    const selecionados = this.colunasValor
      .map(col => this.mapeamentoColunas[col])
      .filter((v): v is number => !!v);
    const temDuplicata = new Set(selecionados).size !== selecionados.length;
    if (temDuplicata) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: 'Cada coluna deve estar vinculada a um tipo de variável diferente.' 
      });
      return;
    }

    // Obter intervalo e depois buscar retrives existentes
    this.obterIntervaloEstacao(() => {
      this.buscarRetrivesExistentes(() => {
        this.processarDadosParaPreview();
        this.passoWizard = 3; // Ir para preview
      });
    });
  }

  buscarRetrivesExistentes(callback?: () => void): void {
    if (!this.estacaoSelecionada || this.dadosProcessados.length === 0) {
      if (callback) callback();
      return;
    }

    // Obter tipos de variável que serão importados
    const tiposVariavel = [...new Set(
      this.colunasValor
        .map(col => this.mapeamentoColunas[col])
        .filter((v): v is number => !!v)
    )];

    if (tiposVariavel.length === 0) {
      if (callback) callback();
      return;
    }

    // Encontrar o intervalo de datas dos dados processados
    let dataInicial: moment.Moment | null = null;
    let dataFinal: moment.Moment | null = null;

    // Processar uma amostra para encontrar o intervalo
    const normalizarNome = (nome: string): string => {
      return nome.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');
    };

    const colDataHora = this.colunasArquivo.find(col => {
      const colNormalizada = normalizarNome(col);
      return colNormalizada.includes('data/hora') || 
             colNormalizada.includes('datahora') ||
             (colNormalizada.includes('data') && colNormalizada.includes('hora'));
    });

    const colData = this.colunasArquivo.find(col => {
      const colNormalizada = normalizarNome(col);
      return (colNormalizada === 'data' || colNormalizada === 'date') && 
             !colNormalizada.includes('hora') && 
             !colNormalizada.includes('time');
    });

    const colHora = this.colunasArquivo.find(col => {
      const colNormalizada = normalizarNome(col);
      return (colNormalizada === 'hora' || colNormalizada === 'time' || colNormalizada === 'hour') && 
             !colNormalizada.includes('data') && 
             !colNormalizada.includes('date');
    });

    // Processar algumas linhas para encontrar intervalo
    this.dadosProcessados.slice(0, Math.min(100, this.dadosProcessados.length)).forEach(linha => {
      let dataHora: moment.Moment | null = null;
      
      if (colDataHora && linha[colDataHora]) {
        const valor = linha[colDataHora];
        if (typeof valor === 'string') {
          dataHora = moment(valor, ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'YYYY-MM-DD HH:mm:ss'], true);
        }
      } else if (colData && colHora && linha[colData] && linha[colHora]) {
        const dataStr = String(linha[colData]);
        const horaStr = String(linha[colHora]);
        dataHora = moment(`${dataStr} ${horaStr}`, ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm'], true);
      }

      if (dataHora && dataHora.isValid()) {
        if (!dataInicial || dataHora.isBefore(dataInicial)) {
          dataInicial = dataHora.clone();
        }
        if (!dataFinal || dataHora.isAfter(dataFinal)) {
          dataFinal = dataHora.clone();
        }
      }
    });

    if (!dataInicial || !dataFinal) {
      if (callback) callback();
      return;
    }

    this.retriveService.consultar({
      estacao_id: this.estacaoSelecionada,
      data_inicial: dataInicial.format('YYYY-MM-DD HH:mm:ss'),
      data_final: dataFinal.format('YYYY-MM-DD HH:mm:ss'),
      tipo_variavel_id: tiposVariavel
    }).subscribe({
      next: (res) => {
        this.retrivesExistentes.clear();
        const retrives = this.corpoConsulta(res).retrives || [];
        
        // Filtrar apenas os tipos de variável que serão importados
        const tiposVariavelSet = new Set(tiposVariavel);
        
        retrives.forEach((retrive: any) => {
          // Verificar se o tipo de variável está nos tipos que serão importados
          if (tiposVariavelSet.has(retrive.tipo_variavel_id)) {
            const key = `${retrive.estacao_id}|${retrive.tipo_variavel_id}|${moment(retrive.data_hora_dado).format('YYYY-MM-DD HH:mm:ss')}`;
            this.retrivesExistentes.set(key, {
              id: retrive.id,
              valor: retrive.valor
            });
          }
        });

        if (callback) callback();
      },
      error: (err) => {
        console.error('Erro ao buscar retrives existentes:', err);
        // Continuar mesmo com erro
        if (callback) callback();
      }
    });
  }

  processarDadosParaPreview(): void {
    this.previewData = [];
    
    // Identificar colunas de data/hora (normalizar nomes removendo espaços e acentos)
    const normalizarNome = (nome: string): string => {
      return nome.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');
    };
    // Converter número serial do Excel para moment (data e hora)
    const excelSerialParaMoment = (serial: number): moment.Moment => {
      return moment('1899-12-30').add(serial, 'days'); // base do Excel
    };
    // Converter número de tempo do Excel (fração de dia) para horas/minutos/segundos
    const excelNumeroParaHora = (valor: number) => {
      const totalSegundos = Math.round(valor * 24 * 60 * 60);
      const horas = Math.floor(totalSegundos / 3600);
      const minutos = Math.floor((totalSegundos % 3600) / 60);
      const segundos = totalSegundos % 60;
      return { horas, minutos, segundos };
    };
    
    const colDataHora = this.colunasArquivo.find(col => {
      const colNormalizada = normalizarNome(col);
      return colNormalizada.includes('data/hora') || 
             colNormalizada.includes('datahora') ||
             (colNormalizada.includes('data') && colNormalizada.includes('hora'));
    });
    
    // Buscar coluna de data (pode ser "data", "date", etc.)
    const colData = this.colunasArquivo.find(col => {
      const colNormalizada = normalizarNome(col);
      return (colNormalizada === 'data' || colNormalizada === 'date') && 
             !colNormalizada.includes('hora') && 
             !colNormalizada.includes('time');
    });
    
    // Buscar coluna de hora (pode ser "hora", "time", "hour", etc.)
    const colHora = this.colunasArquivo.find(col => {
      const colNormalizada = normalizarNome(col);
      return (colNormalizada === 'hora' || colNormalizada === 'time' || colNormalizada === 'hour') && 
             !colNormalizada.includes('data') && 
             !colNormalizada.includes('date');
    });
    
    // Função para parse de data APENAS formato brasileiro (DD/MM/YYYY)
    const parseDataBrasileira = (dataStr: string): moment.Moment | null => {
      // Primeiro tentar formato brasileiro DD/MM/YYYY
      const partes = dataStr.split(/[\/\-]/);
      if (partes.length === 3) {
        const dia = parseInt(partes[0], 10);
        const mes = parseInt(partes[1], 10);
        const ano = parseInt(partes[2], 10);
        
        // Validar se faz sentido como formato brasileiro (dia <= 31, mês <= 12)
        if (!isNaN(dia) && !isNaN(mes) && !isNaN(ano) && dia <= 31 && mes <= 12) {
          // SEMPRE usar formato brasileiro DD/MM/YYYY
          // Tentar primeiro com barra (formato mais comum)
          const dataBR = moment(`${dia.toString().padStart(2, '0')}/${mes.toString().padStart(2, '0')}/${ano}`, 'DD/MM/YYYY', true);
          if (dataBR.isValid()) {
            return dataBR;
          }
          
          // Se não funcionou com barras, tentar com hífen
          const dataBRHifen = moment(`${dia.toString().padStart(2, '0')}-${mes.toString().padStart(2, '0')}-${ano}`, 'DD-MM-YYYY', true);
          if (dataBRHifen.isValid()) {
            return dataBRHifen;
          }
        }
      }
      
      // Tentar apenas formatos brasileiros
      const formatosBR = ['DD/MM/YYYY', 'DD-MM-YYYY', 'DD/MM/YY', 'DD-MM-YY'];
      for (const formato of formatosBR) {
        const data = moment(dataStr, formato, true);
        if (data.isValid()) {
          return data;
        }
      }
      
      return null;
    };
    
    // Função para parse de data/hora APENAS formato brasileiro (DD/MM/YYYY)
    const parseDataHoraBrasileira = (dataHoraStr: string): moment.Moment | null => {
      // Separar data e hora
      const partes = dataHoraStr.trim().split(/\s+/);
      if (partes.length >= 2) {
        const dataStr = partes[0];
        const horaStr = partes.slice(1).join(' ');
        
        // Parse manual da data no formato brasileiro
        const dataPartes = dataStr.split(/[\/\-]/);
        if (dataPartes.length === 3) {
          const dia = parseInt(dataPartes[0], 10);
          const mes = parseInt(dataPartes[1], 10);
          const ano = parseInt(dataPartes[2], 10);
          
          if (!isNaN(dia) && !isNaN(mes) && !isNaN(ano) && dia <= 31 && mes <= 12) {
            // SEMPRE usar formato brasileiro DD/MM/YYYY
            const dataFormatada = `${dia.toString().padStart(2, '0')}/${mes.toString().padStart(2, '0')}/${ano}`;
            const dataHoraCompleta = `${dataFormatada} ${horaStr}`;
            
            // Tentar apenas formatos brasileiros
            const formatosBR = [
              'DD/MM/YYYY HH:mm:ss',
              'DD/MM/YYYY HH:mm',
              'DD-MM-YYYY HH:mm:ss',
              'DD-MM-YYYY HH:mm'
            ];
            
            for (const formato of formatosBR) {
              const dataHora = moment(dataHoraCompleta, formato, true);
              if (dataHora.isValid()) {
                return dataHora;
              }
            }
          }
        }
      }
      
      // Se não funcionou com parse manual, tentar apenas formatos brasileiros
      const formatosBR = [
        'DD/MM/YYYY HH:mm:ss',
        'DD/MM/YYYY HH:mm',
        'DD-MM-YYYY HH:mm:ss',
        'DD-MM-YYYY HH:mm',
        'DD/MM/YY HH:mm:ss',
        'DD/MM/YY HH:mm',
        'DD-MM-YY HH:mm:ss',
        'DD-MM-YY HH:mm'
      ];
      
      for (const formato of formatosBR) {
        const dataHora = moment(dataHoraStr, formato, true);
        if (dataHora.isValid()) {
          return dataHora;
        }
      }
      
      return null;
    };

    console.log('Colunas identificadas:', { 
      colDataHora, 
      colData, 
      colHora, 
      colunasArquivo: this.colunasArquivo,
      colunasValor: this.colunasValor,
      mapeamentoColunas: this.mapeamentoColunas
    });
    console.log('Dados processados (primeiras 3 linhas):', this.dadosProcessados.slice(0, 3));
    console.log('Total de linhas processadas:', this.dadosProcessados.length);

    // Função para converter data string BR (dd/mm/yyyy ou dd/mm/yy) para yyyy-mm-dd
    const converterDataBRParaISO = (dataStr: string): string | null => {
      // Remover espaços e limpar
      dataStr = dataStr.trim();
      
      // Separar por / ou -
      const partes = dataStr.split(/[\/\-]/);
      if (partes.length !== 3) {
        return null;
      }
      
      const dia = parseInt(partes[0], 10);
      const mes = parseInt(partes[1], 10);
      let ano = parseInt(partes[2], 10);
      
      // Validar
      if (isNaN(dia) || isNaN(mes) || isNaN(ano) || dia < 1 || dia > 31 || mes < 1 || mes > 12) {
        return null;
      }
      
      // Se ano tem 2 dígitos, assumir 2000-2099
      if (ano < 100) {
        ano = ano < 50 ? 2000 + ano : 1900 + ano;
      }
      
      // Converter para formato ISO: yyyy-mm-dd
      return `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    };

    this.dadosProcessados.forEach((linha, index) => {
      let dataHora: moment.Moment | null = null;
      
      // Tentar obter data/hora - ler como string e converter
      if (colDataHora) {
        const valor = linha[colDataHora];
        if (valor) {
          // Se for número (serial do Excel), converter
          if (typeof valor === 'number') {
            dataHora = excelSerialParaMoment(valor);
          } else {
            // Ler como string no formato BR: DD/MM/YYYY HH:mm:ss ou DD/MM/YYYY HH:mm
            const valorStr = String(valor).trim();
            const partes = valorStr.split(/\s+/);
            
            if (partes.length >= 2) {
              const dataStr = partes[0];
              const horaStr = partes.slice(1).join(' ');
              
              // Converter data BR para ISO
              const dataISO = converterDataBRParaISO(dataStr);
              if (dataISO) {
                // Juntar data ISO + hora e converter para moment
                const dataHoraISO = `${dataISO} ${horaStr}`;
                dataHora = moment(dataHoraISO, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'], true);
              }
            } else {
              // Tentar parse direto assumindo formato BR
              dataHora = moment(valorStr, ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm'], true);
            }
          }
        }
      } else if (colData && colHora) {
        const data = linha[colData];
        const hora = linha[colHora];
        
        if (data && hora) {
          // Ler data como string (assumindo formato BR: dd/mm/yyyy ou dd/mm/yy)
          let dataStr = String(data).trim();
          let horaStr = String(hora).trim();
          
          // Se a data vier como objeto Date do Excel, converter para string BR
          if (data instanceof Date) {
            const dia = String(data.getDate()).padStart(2, '0');
            const mes = String(data.getMonth() + 1).padStart(2, '0');
            const ano = data.getFullYear();
            dataStr = `${dia}/${mes}/${ano}`;
          } else if (typeof data === 'number') {
            // Se for número serial do Excel, converter para string BR
            const dataMoment = excelSerialParaMoment(data);
            dataStr = dataMoment.format('DD/MM/YYYY');
          }
          
          // Se a hora vier como número (fração do dia do Excel), converter para string
          if (typeof hora === 'number') {
            const { horas, minutos, segundos } = excelNumeroParaHora(hora);
            horaStr = `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}:${String(segundos).padStart(2, '0')}`;
          }
          
          // Converter data string BR (dd/mm/yyyy ou dd/mm/yy) para yyyy-mm-dd
          const dataISO = converterDataBRParaISO(dataStr);
          if (dataISO) {
            // Juntar data ISO + hora e converter para moment (formato BR)
            const dataHoraISO = `${dataISO} ${horaStr}`;
            dataHora = moment(dataHoraISO, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'], true);
          }
        }
      }
      
      if (!dataHora || !dataHora.isValid()) {
        console.warn(`Linha ${linha._linha || index + 2}: Data/hora inválida`, { 
          colData, 
          colHora, 
          colDataHora,
          data: linha[colData || ''], 
          hora: linha[colHora || ''],
          dataHora: linha[colDataHora || ''],
          linhaCompleta: linha
        });
        return; // Pular linhas sem data/hora válida
      }

      const dataHoraBrasilia = this.ajustarHorarioBrasilia(dataHora);

      // Processar cada coluna de valor
      this.colunasValor.forEach(col => {
        const valor = linha[col];
        const tipoVariavelId = this.mapeamentoColunas[col];
        
        if (tipoVariavelId && valor !== '' && valor !== null && valor !== undefined) {
          // Converter valor numérico (suporta vírgula ou ponto como separador decimal)
          let valorStr = String(valor).trim();
          const dataHoraFormatada = dataHoraBrasilia.format('YYYY-MM-DD HH:mm:ss');
          const keyExistente = `${this.estacaoSelecionada}|${tipoVariavelId}|${dataHoraFormatada}`;
          const retriveExistente = this.retrivesExistentes.get(keyExistente);
          
          // Se for número, converter diretamente
          if (typeof valor === 'number') {
            this.previewData.push({
              estacao_id: this.estacaoSelecionada,
              tipo_variavel_id: tipoVariavelId,
              data_hora_dado: dataHoraFormatada,
              valor: valor,
              _linha: linha._linha,
              _coluna: col,
              _existente: !!retriveExistente,
              _retrive_id: retriveExistente?.id || null
            });
          } else {
            // Converter string para número (suporta vírgula ou ponto como separador decimal)
            valorStr = valorStr.replace(',', '.');
            const valorNum = parseFloat(valorStr);
            
            if (!isNaN(valorNum)) {
              this.previewData.push({
                estacao_id: this.estacaoSelecionada,
                tipo_variavel_id: tipoVariavelId,
                data_hora_dado: dataHoraFormatada,
                valor: valorNum,
                _linha: linha._linha,
                _coluna: col,
                _existente: !!retriveExistente,
                _retrive_id: retriveExistente?.id || null
              });
            } else {
              console.warn(`Linha ${linha._linha || index + 2}, Coluna ${col}: Valor não numérico`, { 
                valor, 
                valorStr,
                tipo: typeof valor
              });
            }
          }
        }
      });
    });
    
    console.log(`Preview gerado: ${this.previewData.length} registros`);
    if (this.previewData.length > 0) {
      console.log('Primeiros registros do preview:', this.previewData.slice(0, 5));
    }

    // Ordenar por data/hora
    this.previewData.sort((a, b) => {
      return moment(a.data_hora_dado).diff(moment(b.data_hora_dado));
    });

    // Preencher dados faltantes conforme intervalo
    if (this.intervaloEstacao && this.previewData.length > 0) {
      this.preencherDadosFaltantes();
    }
  }

  preencherDadosFaltantes(): void {
    if (!this.intervaloEstacao || this.previewData.length === 0) return;

    const dadosCompletos: any[] = [];
    const tiposVariavel = [...new Set(this.previewData.map(d => d.tipo_variavel_id))];
    
    tiposVariavel.forEach(tipoVariavelId => {
      const dadosTipo = this.previewData.filter(d => d.tipo_variavel_id === tipoVariavelId).sort((a, b) => {
        return moment(a.data_hora_dado).diff(moment(b.data_hora_dado));
      });
      
      if (dadosTipo.length === 0) return;
      
      const primeiraData = moment(dadosTipo[0].data_hora_dado);
      const ultimaData = moment(dadosTipo[dadosTipo.length - 1].data_hora_dado);
      
      // Arredondar primeira data para o intervalo
      const minutosPrimeira = primeiraData.minute();
      const resto = minutosPrimeira % this.intervaloEstacao;
      const primeiraDataAjustada = primeiraData.clone().subtract(resto, 'minutes');
      
      let dataAtual = primeiraDataAjustada.clone();
      let indiceDados = 0;
      
      while (dataAtual.isSameOrBefore(ultimaData)) {
        const dataAtualStr = dataAtual.format('YYYY-MM-DD HH:mm:ss');
        const dadoExistente = dadosTipo[indiceDados];
        
        if (dadoExistente && moment(dadoExistente.data_hora_dado).isSame(dataAtual, 'minute')) {
          dadosCompletos.push(dadoExistente);
          indiceDados++;
        } else {
          // Dado faltante - verificar se já existe
          const keyExistente = `${this.estacaoSelecionada}|${tipoVariavelId}|${dataAtualStr}`;
          const retriveExistente = this.retrivesExistentes.get(keyExistente);
          
          // Dado faltante - adicionar com valor zero
          dadosCompletos.push({
            estacao_id: this.estacaoSelecionada,
            tipo_variavel_id: tipoVariavelId,
            data_hora_dado: dataAtualStr,
            valor: 0,
            _faltante: true,
            _existente: !!retriveExistente,
            _retrive_id: retriveExistente?.id || null
          });
        }
        
        dataAtual.add(this.intervaloEstacao, 'minutes');
      }
    });
    
    this.previewData = dadosCompletos.sort((a, b) => {
      return moment(a.data_hora_dado).diff(moment(b.data_hora_dado));
    });
  }

  importar(): void {
    if (this.previewData.length === 0) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'Atenção', 
        detail: 'Nenhum dado para importar' 
      });
      return;
    }

    // Verificar se há dados existentes
    const dadosExistentes = this.previewData.filter(d => d._existente);
    const totalExistentes = dadosExistentes.length;

    if (totalExistentes > 0) {
      // Mostrar confirmação
      this.confirmationService.confirm({
        key: 'globalConfirm',
        message: `Existem ${totalExistentes} registro(s) que já estão cadastrados para esta estação, variável e período. Estes dados serão substituídos pelos novos valores. Deseja continuar?`,
        header: 'Confirmar Substituição',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sim, substituir',
        rejectLabel: 'Cancelar',
        accept: () => {
          this.executarImportacao();
        }
      });
    } else {
      // Não há dados existentes, importar diretamente
      this.executarImportacao();
    }
  }

  executarImportacao(): void {
    this.loading = true;

    // Separar dados existentes (PUT) e novos (POST)
    const dadosParaAtualizar: any[] = [];
    const dadosParaCriar: any[] = [];

    this.previewData.forEach(d => {
      const retriveData = {
        estacao_id: d.estacao_id,
        tipo_variavel_id: d.tipo_variavel_id,
        data_hora_dado: d.data_hora_dado,
        valor: d.valor,
        status: 0
      };

      if (d._existente && d._retrive_id) {
        // Dado existente - usar PUT
        dadosParaAtualizar.push({
          id: d._retrive_id,
          ...retriveData
        });
      } else {
        // Dado novo - usar POST
        dadosParaCriar.push(retriveData);
      }
    });

    // Executar atualizações (PUT) e criações (POST) em paralelo
    const operacoes: Promise<any>[] = [];

    // Atualizar dados existentes
    dadosParaAtualizar.forEach(dado => {
      operacoes.push(
        this.retriveService.update(dado, dado.id).toPromise()
      );
    });

    // Criar novos dados (se houver)
    if (dadosParaCriar.length > 0) {
      const dados = {
        data_hora_importacao: moment().format('YYYY-MM-DD HH:mm:ss'),
        usuario_id: this.user.id,
        status: 0,
        retrives: dadosParaCriar
      };

      operacoes.push(
        this.retriveImportacaoService.criarComRetrives(dados).toPromise()
      );
    }

    // Aguardar todas as operações
    Promise.all(operacoes)
      .then((results) => {
        const totalAtualizados = dadosParaAtualizar.length;
        const totalCriados = dadosParaCriar.length;
        const totalProcessados = totalAtualizados + totalCriados;

        let mensagem = `${totalProcessados} dado(s) processado(s) com sucesso`;
        if (totalAtualizados > 0 && totalCriados > 0) {
          mensagem += ` (${totalAtualizados} atualizado(s) e ${totalCriados} criado(s))`;
        } else if (totalAtualizados > 0) {
          mensagem += ` (${totalAtualizados} atualizado(s))`;
        } else if (totalCriados > 0) {
          mensagem += ` (${totalCriados} criado(s))`;
        }

        this.messageService.add({ 
          severity: 'success', 
          summary: 'Sucesso', 
          detail: mensagem
        });
        this.loading = false;
        this.resetarImportacao();
        this.index();
      })
      .catch((err) => {
        const errorMsg = err?.error?.message || 'Erro ao importar dados';
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Erro', 
          detail: errorMsg 
        });
        this.loading = false;
      });
  }

  abrirWizard(): void {
    this.resetarImportacao();
    this.mostrarWizard = true;
    this.passoWizard = 1;
    // Aguardar o modal abrir antes de inicializar o FileUpload
    setTimeout(() => {
      if (this.fileUpload) {
        this.fileUpload.clear();
      }
    }, 300);
  }

  resetarImportacao(): void {
    this.arquivoSelecionado = null;
    this.estacaoSelecionada = null;
    this.dadosProcessados = [];
    this.previewData = [];
    this.colunasArquivo = [];
    this.mapeamentoColunas = {};
    this.colunasValor = [];
    this.passoWizard = 1;
    this.intervaloEstacao = null;
    this.processandoArquivo = false;
    this.retrivesExistentes.clear();
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }

  exportarComparativo(importacao: any): void {
    this.loading = true;

    this.retriveService.consultar({
      retrive_importacao_id: importacao.id,
      com_coleta: true
    }).subscribe({
      next: (res) => {
        const body = this.corpoConsulta(res);
        const retrivesData = body.retrives || [];

        if (retrivesData.length === 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Nenhum dado encontrado para esta importação'
          });
          this.loading = false;
          return;
        }

        this.gerarExcelComparativo(retrivesData, body.dados_variavel || [], importacao);
        this.loading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao buscar dados para exportação'
        });
        this.loading = false;
      }
    });
  }

  gerarExcelComparativo(retrives: any[], dadosVariavel: { data_hora_dado: string, tipo_variavel_id: number, valor: number }[], importacao: any): void {
    // Agrupar dados por data/hora e tipo de variável
    const dadosAgrupados: { [key: string]: any } = {};
    
    // Coletar todos os tipos de variável únicos
    const tiposVariavelSet = new Set<number>();
    retrives.forEach((retrive: any) => {
      if (retrive.tipo_variavel_id) {
        tiposVariavelSet.add(retrive.tipo_variavel_id);
      }
    });
    dadosVariavel.forEach((dado: any) => {
      if (dado.tipo_variavel_id) {
        tiposVariavelSet.add(dado.tipo_variavel_id);
      }
    });
    
    // Ordenar tipos de variável por ID
    const tiposVariavelOrdenados = Array.from(tiposVariavelSet).sort((a, b) => a - b);
    
    // Criar ordem de colunas: para cada tipo, primeiro Dados_Variavel, depois Retrive
    const ordemColunas: string[] = ['Data/Hora'];
    tiposVariavelOrdenados.forEach(tipoId => {
      const tipoNome = this.getTipoVariavelNome(tipoId);
      ordemColunas.push(`Dados_Variavel_${tipoNome}`);
      ordemColunas.push(`Retrive_${tipoNome}`);
    });
    
    retrives.forEach((retrive: any) => {
      const key = moment(retrive.data_hora_dado).format('YYYY-MM-DD HH:mm:ss');
      if (!dadosAgrupados[key]) {
        dadosAgrupados[key] = {
          data_hora: key,
          valores: {}
        };
      }
      const tipoNome = retrive.tipoVariavel?.tipo_variavel || this.getTipoVariavelNome(retrive.tipo_variavel_id);
      dadosAgrupados[key].valores[`Retrive_${tipoNome}`] = retrive.valor;
    });
    
    dadosVariavel.forEach((dado: any) => {
      const key = moment(dado.data_hora_dado).format('YYYY-MM-DD HH:mm:ss');
      if (!dadosAgrupados[key]) {
        dadosAgrupados[key] = {
          data_hora: key,
          valores: {}
        };
      }
      const tipoNome = this.getTipoVariavelNome(dado.tipo_variavel_id);
      dadosAgrupados[key].valores[`Dados_Variavel_${tipoNome}`] = dado.valor;
    });
    
    // Converter para array e ordenar, mantendo a ordem das colunas
    const dadosExport = Object.values(dadosAgrupados)
      .map((item: any) => {
        const linha: any = {};
        
        // Criar linha seguindo a ordem definida
        ordemColunas.forEach(coluna => {
          if (coluna === 'Data/Hora') {
            linha[coluna] = moment(item.data_hora).format('DD/MM/YYYY HH:mm:ss');
          } else {
            const valor = item.valores[coluna];
            // Verificar se é coluna de Retrive ou Dados_Variavel
            if (coluna.startsWith('Retrive_')) {
              // Se não houver valor no retrive, exibir "sem retrive"
              // Considera que 0 é um valor válido, então só trata null, undefined ou string vazia
              if (valor === null || valor === undefined || valor === '') {
                linha[coluna] = 'sem retrive';
              } else {
                linha[coluna] = valor;
              }
            } else if (coluna.startsWith('Dados_Variavel_')) {
              // Se não houver valor nos dados da variável, exibir "sem coleta"
              // Considera que 0 é um valor válido, então só trata null, undefined ou string vazia
              if (valor === null || valor === undefined || valor === '') {
                linha[coluna] = 'sem coleta';
              } else {
                linha[coluna] = valor;
              }
            } else {
              linha[coluna] = valor || null;
            }
          }
        });
        
        return linha;
      })
      .sort((a: any, b: any) => {
        return moment(a['Data/Hora'], 'DD/MM/YYYY HH:mm:ss').diff(moment(b['Data/Hora'], 'DD/MM/YYYY HH:mm:ss'));
      });
    
    // Função para comparar valores (considerando números e strings)
    const valoresDiferentes = (valor1: any, valor2: any): boolean => {
      // Não destacar se algum valor for nulo, indefinido ou mensagem informativa
      if (valor1 === null || valor1 === undefined || valor2 === null || valor2 === undefined) {
        return false;
      }
      
      // Não destacar se algum valor for mensagem informativa
      const str1 = String(valor1).trim().toLowerCase();
      const str2 = String(valor2).trim().toLowerCase();
      if (str1 === 'sem retrive' || str1 === 'sem coleta' || str2 === 'sem retrive' || str2 === 'sem coleta') {
        return false;
      }
      
      // Converter para número se possível
      const num1 = typeof valor1 === 'number' ? valor1 : parseFloat(valor1);
      const num2 = typeof valor2 === 'number' ? valor2 : parseFloat(valor2);
      
      // Se ambos são números válidos, comparar numericamente
      if (!isNaN(num1) && !isNaN(num2)) {
        return Math.abs(num1 - num2) > 0.0001; // Tolerância para diferenças de ponto flutuante
      }
      
      // Caso contrário, comparar como string
      return str1 !== str2;
    };
    
    // Exportar para Excel - tentar usar exceljs primeiro (suporta estilos)
    // Se não estiver disponível, usar XLSX padrão
    import('exceljs').then((ExcelJSModule: any) => {
      if (ExcelJSModule && ExcelJSModule.Workbook) {
        // Usar ExcelJS (suporta estilos)
        const ExcelJS = ExcelJSModule;
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Comparativo');
        
        // Adicionar cabeçalhos
        const headers = ['Data/Hora'];
        tiposVariavelOrdenados.forEach(tipoId => {
          const tipoNome = this.getTipoVariavelNome(tipoId);
          headers.push(`Dados_Variavel_${tipoNome}`, `Retrive_${tipoNome}`);
        });
        const headerRow = worksheet.addRow(headers);
        
        // Estilizar cabeçalho
        headerRow.font = { bold: true };
        headerRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' }
        };
        
        // Adicionar dados
        dadosExport.forEach((linha: any) => {
          const rowData: any[] = [linha['Data/Hora']];
          tiposVariavelOrdenados.forEach(tipoId => {
            const tipoNome = this.getTipoVariavelNome(tipoId);
            const colDadosVariavel = `Dados_Variavel_${tipoNome}`;
            const colRetrive = `Retrive_${tipoNome}`;
            rowData.push(linha[colDadosVariavel] ?? null, linha[colRetrive] ?? null);
          });
          
          const row = worksheet.addRow(rowData);
          
          // Verificar diferenças e aplicar cor
          tiposVariavelOrdenados.forEach((tipoId, tipoIndex) => {
            const tipoNome = this.getTipoVariavelNome(tipoId);
            const colDadosVariavel = `Dados_Variavel_${tipoNome}`;
            const colRetrive = `Retrive_${tipoNome}`;
            
            const valorDados = linha[colDadosVariavel];
            const valorRetrive = linha[colRetrive];
            
            // Calcular índices das colunas (0 para Data/Hora, depois 2 colunas por tipo)
            // No array: 0=Data/Hora, 1=Dados_Variavel_1, 2=Retrive_1, 3=Dados_Variavel_2, 4=Retrive_2, etc.
            const colDadosIndex = 1 + (tipoIndex * 2); // Primeira coluna do tipo (índice no array)
            const colRetriveIndex = 2 + (tipoIndex * 2); // Segunda coluna do tipo (índice no array)
            
            // Se os valores forem diferentes, aplicar fundo vermelho claro
            // ExcelJS usa índice baseado em 1, então adicionamos 1 ao índice do array
            if (valoresDiferentes(valorDados, valorRetrive)) {
              const cellDados = row.getCell(colDadosIndex + 1);
              const cellRetrive = row.getCell(colRetriveIndex + 1);
              
              if (cellDados && cellRetrive) {
                cellDados.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: 'FFFFCCCC' } // Vermelho claro
                };
                
                cellRetrive.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: 'FFFFCCCC' } // Vermelho claro
                };
              }
            }
          });
        });
        
        // Ajustar largura das colunas
        worksheet.columns.forEach((column: any, index: number) => {
          if (column) {
            if (index === 0) {
              column.width = 20; // Data/Hora
            } else {
              column.width = 15; // Colunas de valores
            }
          }
        });
        
        // Gerar buffer e salvar
        return workbook.xlsx.writeBuffer().then((buffer: any) => {
          this.saveAsExcelFile(buffer, `Comparativo_Importacao_${importacao.id}`);
        });
      } else {
        throw new Error('ExcelJS not available');
      }
    }).catch(() => {
      // Fallback para XLSX padrão (sem estilos)
      return import('xlsx').then(xlsx => {
        const worksheet = xlsx.utils.json_to_sheet(dadosExport, { header: ordemColunas });
        const workbook = { Sheets: { 'Comparativo': worksheet }, SheetNames: ['Comparativo'] };
        const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, `Comparativo_Importacao_${importacao.id}`);
        
        this.messageService.add({
          severity: 'warn',
          summary: 'Aviso',
          detail: 'Para visualizar diferenças coloridas no Excel, instale: npm install exceljs'
        });
      });
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    FileSaver.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
  }

  baixarModeloCsv(): void {
    const separador = ';';
    const tiposParaModelo = this.dropDownTipoVariavel.slice(0, 3).map((t: any) => t?.label).filter((t: any) => !!t);
    const colunasVariavel = tiposParaModelo.length > 0 ? tiposParaModelo : ['Nivel', 'Bateria'];

    const cabecalho = ['Data', 'Hora', ...colunasVariavel];
    const linhas = [
      ['24/04/2026', '00:00:00', ...colunasVariavel.map((_, i) => i === 0 ? '12.34' : i === 1 ? '98.7' : '0.0')],
      ['24/04/2026', '00:10:00', ...colunasVariavel.map((_, i) => i === 0 ? '12.10' : i === 1 ? '98.6' : '0.0')],
      ['24/04/2026', '00:20:00', ...colunasVariavel.map((_, i) => i === 0 ? '11.98' : i === 1 ? '98.5' : '0.0')]
    ];

    const conteudo = [
      cabecalho.join(separador),
      ...linhas.map((linha) => linha.join(separador))
    ].join('\r\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + conteudo], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, `modelo_importacao_retrive_${moment().format('YYYYMMDD_HHmmss')}.csv`);

    this.messageService.add({
      severity: 'info',
      summary: 'Modelo gerado',
      detail: 'CSV de exemplo baixado. Use Data/Hora em formato brasileiro e valores numéricos.'
    });
  }

  baixarModeloXlsx(): void {
    const url = 'assets/modelos/modelo_importacao_retrive.xlsx';
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error('Arquivo de modelo não encontrado');
        }
        return response.blob();
      })
      .then(blob => {
        FileSaver.saveAs(blob, 'modelo_importacao_retrive.xlsx');
      })
      .catch((error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível baixar o modelo XLSX: ' + error.message
        });
      });
  }

  abrirModalGrafico(importacao: any): void {
    this.modalGrafico = true;
    this.graficoVariavelSelecionada = null;
    this.dadosComparativos = [];
    
    this.retriveService.consultar({
      retrive_importacao_id: importacao.id,
      com_coleta: true
    }).subscribe({
      next: (res) => {
        const body = this.corpoConsulta(res);
        const retrivesData = body.retrives || [];

        if (retrivesData.length === 0 && (body.dados_variavel || []).length === 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Nenhum dado encontrado para esta importação'
          });
          return;
        }

        this.dadosComparativos = {
          retrives: retrivesData,
          dadosVariavel: body.dados_variavel || [],
          tipoVariavel: retrivesData[0]?.tipoVariavel || null
        };

        const tipos = [...new Set(retrivesData.map((r: any) => r.tipo_variavel_id))];
        if (tipos.length > 0) {
          this.graficoVariavelSelecionada = Number(tipos[0]);
          setTimeout(() => this.criarGrafico(), 100);
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao buscar dados da estação para gráfico'
        });
      }
    });
  }

  criarGrafico(): void {
    if (!this.chartContainer || !this.graficoVariavelSelecionada || !this.dadosComparativos) return;
    
    const dadosComparativosAny = this.dadosComparativos as any;
    const dadosRetrive = (dadosComparativosAny.retrives || []).filter((r: any) => 
      r.tipo_variavel_id === this.graficoVariavelSelecionada
    ).sort((a: any, b: any) => moment(a.data_hora_dado).diff(moment(b.data_hora_dado)));
    
    const dadosVariavel = (dadosComparativosAny.dadosVariavel || []).filter((d: any) => 
      d.tipo_variavel_id === this.graficoVariavelSelecionada
    ).sort((a: any, b: any) => moment(a.data_hora_dado).diff(moment(b.data_hora_dado)));
    
    this.descartarGrafico();

    this.chartInstance = echarts.init(this.chartContainer.nativeElement);
    window.addEventListener('resize', this.onWindowResize);
    
    // Criar mapa de dados por data/hora
    const mapaRetrive: { [key: string]: number } = {};
    dadosRetrive.forEach((r: any) => {
      const key = moment(r.data_hora_dado).format('YYYY-MM-DD HH:mm');
      mapaRetrive[key] = r.valor;
    });
    
    const mapaVariavel: { [key: string]: number } = {};
    dadosVariavel.forEach((d: any) => {
      const key = moment(d.data_hora_dado).format('YYYY-MM-DD HH:mm');
      mapaVariavel[key] = d.valor;
    });
    
    // Gerar todas as datas/horas esperadas baseado no intervalo_operacao
    let todasDatas: string[] = [];
    
    if (this.intervaloOperacaoAnalise && this.periodoAnalise && this.periodoAnalise.length === 2) {
      // Gerar períodos baseados no intervalo_operacao
      const dataInicial = moment(this.periodoAnalise[0]).startOf('day');
      const dataFinal = moment(this.periodoAnalise[1]).endOf('day');
      const intervaloMinutos = this.intervaloOperacaoAnalise;
      
      let dataAtual = dataInicial.clone();
      
      // Arredondar para o primeiro intervalo do dia
      const minutosPrimeiro = dataAtual.minute();
      const resto = minutosPrimeiro % intervaloMinutos;
      if (resto > 0) {
        dataAtual.subtract(resto, 'minutes');
      }
      
      // Criar todos os horários esperados
      while (dataAtual.isSameOrBefore(dataFinal)) {
        const key = dataAtual.format('YYYY-MM-DD HH:mm');
        todasDatas.push(key);
        dataAtual.add(intervaloMinutos, 'minutes');
      }
    } else {
      // Fallback: usar apenas as datas que existem nos dados
      todasDatas = [...new Set([
        ...Object.keys(mapaRetrive),
        ...Object.keys(mapaVariavel)
      ])].sort();
    }
    
    const valoresRetrive = todasDatas.map(key => {
      const valor = mapaRetrive[key];
      return valor !== undefined ? valor : null;
    });
    
    const valoresVariavel = todasDatas.map(key => {
      const valor = mapaVariavel[key];
      return valor !== undefined ? valor : null;
    });
    
    // Calcular diferenças e faixas para destaque (apenas quando ambos valores existem)
    const faixaInferior = todasDatas.map((key, index) => {
      const retrive = valoresRetrive[index];
      const variavel = valoresVariavel[index];
      // Só calcular se ambos valores forem numéricos (não null)
      if (retrive !== null && variavel !== null && typeof retrive === 'number' && typeof variavel === 'number') {
        return Math.min(retrive, variavel);
      }
      return null;
    });
    const faixaDelta = todasDatas.map((key, index) => {
      const retrive = valoresRetrive[index];
      const variavel = valoresVariavel[index];
      // Só calcular se ambos valores forem numéricos (não null)
      if (retrive !== null && variavel !== null && typeof retrive === 'number' && typeof variavel === 'number') {
        return Math.abs(retrive - variavel);
      }
      return null;
    });
    
    const option = {
      title: {
        text: (dadosComparativosAny.tipoVariavel?.tipo_variavel || 'Comparativo') + ' - Retrive vs Dados Variável',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        formatter: (params: any) => {
          let result = `<div style="margin-bottom: 5px;"><strong>${params[0].axisValue}</strong></div>`;
          params.forEach((param: any) => {
            let valor = param.value;
            let label = param.seriesName;
            
            if (valor === null || valor === undefined) {
              if (label === 'Retrive') {
                valor = '(sem retrive)';
              } else if (label === 'Dados Variável') {
                valor = '(sem coleta)';
              } else {
                valor = '-';
              }
            }
            
            result += `<div style="margin: 2px 0;">
              <span style="display:inline-block;width:10px;height:10px;background-color:${param.color};border-radius:50%;margin-right:5px;"></span>
              ${label}: <strong>${valor}</strong>
            </div>`;
          });
          return result;
        }
      },
      legend: {
        data: ['Retrive', 'Dados Variável', 'Diferença'],
        bottom: 0
      },
      xAxis: {
        type: 'category',
        data: todasDatas.map(key => moment(key).format('DD/MM HH:mm')),
        axisLabel: {
          rotate: 45
        }
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: 'Retrive',
          type: 'line',
          data: valoresRetrive,
          smooth: true,
          itemStyle: { color: '#42A5F5' }
        },
        {
          name: 'Dados Variável',
          type: 'line',
          data: valoresVariavel,
          smooth: true,
          itemStyle: { color: '#66BB6A' }
        },
        {
          name: 'Base Diferença',
          type: 'line',
          data: faixaInferior,
          lineStyle: { opacity: 0 },
          symbol: 'none',
          stack: 'faixaDiff',
          areaStyle: { opacity: 0 }
        },
        {
          name: 'Diferença',
          type: 'line',
          data: faixaDelta,
          areaStyle: {
            color: 'rgba(255, 0, 0, 0.3)'
          },
          smooth: true,
          lineStyle: {
            opacity: 0
          },
          symbol: 'none',
          stack: 'faixaDiff',
          itemStyle: { color: 'rgba(255, 0, 0, 0.5)' }
        }
      ]
    };
    
    this.chartInstance.setOption(option);
  }

  fecharModalGrafico(): void {
    this.descartarGrafico();
  }

  private descartarGrafico(): void {
    window.removeEventListener('resize', this.onWindowResize);
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  }

  formatarDataHora(dataHora: string): string {
    return moment(dataHora).format('DD/MM/YYYY HH:mm:ss');
  }

  getTipoVariavelNome(tipoId: number): string {
    const tipo = this.dropDownTipoVariavel.find(t => t.value === tipoId);
    return tipo?.label || `Tipo ${tipoId}`;
  }

  getEstacaoNome(estacaoId: number): string {
    const estacao = this.dropDownEstacoes.find(e => Number(e.value) === Number(estacaoId));
    return estacao?.label || `Estação ${estacaoId}`;
  }

  nomeEstacao(item: any): string {
    const descricao = item?.estacao?.descricao
      || item?.retrives?.[0]?.estacao?.descricao;
    if (descricao) {
      return descricao;
    }

    const estacaoId = item?.estacao_id
      || item?.retrives?.[0]?.estacao_id
      || item?.retrives?.[0]?.estacao?.id;

    return estacaoId ? this.getEstacaoNome(estacaoId) : 'N/A';
  }

  abrirModalAnalisePeriodo(): void {
    this.modalAnalisePeriodo = true;
    this.estacaoAnalise = null;
    this.periodoAnalise = [];
    this.intervaloOperacaoAnalise = null;
  }

  carregarIntervaloOperacao(): void {
    this.intervaloOperacaoAnalise = null;
  }

  exportarExcelPeriodo(): void {
    if (!this.estacaoAnalise || !this.periodoAnalise || this.periodoAnalise.length !== 2) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione a estação e o período'
      });
      return;
    }

    this.loading = true;
    const dataInicial = moment(this.periodoAnalise[0]).format('YYYY-MM-DD');
    const dataFinal = moment(this.periodoAnalise[1]).format('YYYY-MM-DD');

    this.retriveService.consultar({
      estacao_id: this.estacaoAnalise,
      data_inicial: dataInicial,
      data_final: dataFinal,
      com_coleta: true
    }).subscribe({
      next: (res) => {
        const body = this.corpoConsulta(res);
        const estacao = body.estacao;
        const periodo = body.periodo || { data_inicial: dataInicial, data_final: dataFinal };

        if (!estacao?.intervalo_operacao) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Intervalo de operação da estação não configurado'
          });
          this.loading = false;
          return;
        }

        this.intervaloOperacaoAnalise = estacao.intervalo_operacao;
        this.gerarExcelComparativoPeriodo(
          body.retrives || [],
          body.dados_variavel || [],
          estacao,
          periodo
        );
        this.loading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao buscar dados do período'
        });
        this.loading = false;
      }
    });
  }

  abrirGraficoPeriodo(): void {
    if (!this.estacaoAnalise || !this.periodoAnalise || this.periodoAnalise.length !== 2) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione a estação e o período'
      });
      return;
    }

    this.loading = true;
    this.modalGrafico = true;
    this.graficoVariavelSelecionada = null;
    this.dadosComparativos = null;

    const dataInicial = moment(this.periodoAnalise[0]).format('YYYY-MM-DD');
    const dataFinal = moment(this.periodoAnalise[1]).format('YYYY-MM-DD');

    this.retriveService.consultar({
      estacao_id: this.estacaoAnalise,
      data_inicial: dataInicial,
      data_final: dataFinal,
      com_coleta: true
    }).subscribe({
      next: (res) => {
        const body = this.corpoConsulta(res);
        const retrivesData = body.retrives || [];
        const dadosNormalizados = body.dados_variavel || [];
        this.intervaloOperacaoAnalise = body.estacao?.intervalo_operacao || null;

        if (retrivesData.length === 0 && dadosNormalizados.length === 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Nenhum dado encontrado para o período selecionado'
          });
          this.loading = false;
          return;
        }

        this.dadosComparativos = {
          retrives: retrivesData,
          dadosVariavel: dadosNormalizados,
          tipoVariavel: retrivesData[0]?.tipoVariavel || null
        };

        const primeiroTipo = retrivesData[0]?.tipo_variavel_id || dadosNormalizados[0]?.tipo_variavel_id;
        if (primeiroTipo) {
          this.graficoVariavelSelecionada = Number(primeiroTipo);
          setTimeout(() => this.criarGrafico(), 100);
        }
        this.loading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao buscar dados do período'
        });
        this.loading = false;
      }
    });
  }

  gerarExcelComparativoPeriodo(
    retrives: any[],
    dadosVariavel: { data_hora_dado: string, tipo_variavel_id: number, valor: number }[],
    estacao: any,
    periodo: { data_inicial: string, data_final: string }
  ): void {
    // Usar intervalo_operacao da estação ou do componente
    const intervaloOperacao = estacao?.intervalo_operacao || this.intervaloOperacaoAnalise;
    
    if (!intervaloOperacao) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Intervalo de operação da estação não configurado'
      });
      return;
    }

    // Coletar todos os tipos de variável únicos primeiro
    const tiposVariavelSet = new Set<number>();
    retrives.forEach((retrive: any) => {
      if (retrive.tipo_variavel_id) {
        tiposVariavelSet.add(retrive.tipo_variavel_id);
      }
    });
    dadosVariavel.forEach((dado: any) => {
      if (dado.tipo_variavel_id) {
        tiposVariavelSet.add(dado.tipo_variavel_id);
      }
    });

    // Se não houver tipos de variável dos dados, buscar da estação
    if (tiposVariavelSet.size === 0 && estacao?.estacao_coletas) {
      estacao.estacao_coletas.forEach((coleta: any) => {
        if (coleta.tipo_variavel_id) {
          tiposVariavelSet.add(coleta.tipo_variavel_id);
        }
      });
    }

    // Se ainda não houver tipos, usar todos os tipos disponíveis
    if (tiposVariavelSet.size === 0) {
      this.dropDownTipoVariavel.forEach((tipo: any) => {
        if (tipo.value) {
          tiposVariavelSet.add(tipo.value);
        }
      });
    }

    // Criar mapa de todos os horários esperados baseado no intervalo_operacao
    const horariosEsperados = new Map<string, Set<number>>();
    const dataInicial = moment(periodo.data_inicial);
    const dataFinal = moment(periodo.data_final).endOf('day');
    const intervaloMinutos = intervaloOperacao;

    let dataAtual = dataInicial.clone().startOf('day');
    
    // Arredondar para o primeiro intervalo do dia
    const minutosPrimeiro = dataAtual.minute();
    const resto = minutosPrimeiro % intervaloMinutos;
    if (resto > 0) {
      dataAtual.subtract(resto, 'minutes');
    }

    // Criar todos os horários esperados para todos os tipos de variável
    while (dataAtual.isSameOrBefore(dataFinal)) {
      const key = dataAtual.format('YYYY-MM-DD HH:mm:ss');
      horariosEsperados.set(key, new Set(tiposVariavelSet));
      dataAtual.add(intervaloMinutos, 'minutes');
    }

    // Agrupar dados por data/hora e tipo de variável
    const dadosAgrupados: { [key: string]: any } = {};

    // Ordenar tipos de variável por ID
    const tiposVariavelOrdenados = Array.from(tiposVariavelSet).sort((a, b) => a - b);

    // Criar ordem de colunas: para cada tipo, primeiro Dados_Variavel, depois Retrive
    const ordemColunas: string[] = ['Data/Hora'];
    tiposVariavelOrdenados.forEach(tipoId => {
      const tipoNome = this.getTipoVariavelNome(tipoId);
      ordemColunas.push(`Dados_Variavel_${tipoNome}`);
      ordemColunas.push(`Retrive_${tipoNome}`);
    });

    // Processar retrives
    retrives.forEach((retrive: any) => {
      const key = moment(retrive.data_hora_dado).format('YYYY-MM-DD HH:mm:ss');
      if (!dadosAgrupados[key]) {
        dadosAgrupados[key] = {
          data_hora: key,
          valores: {}
        };
      }
      const tipoNome = retrive.tipoVariavel?.tipo_variavel || this.getTipoVariavelNome(retrive.tipo_variavel_id);
      dadosAgrupados[key].valores[`Retrive_${tipoNome}`] = retrive.valor;
    });

    // Processar dados da variável
    dadosVariavel.forEach((dado: any) => {
      const key = moment(dado.data_hora_dado).format('YYYY-MM-DD HH:mm:ss');
      if (!dadosAgrupados[key]) {
        dadosAgrupados[key] = {
          data_hora: key,
          valores: {}
        };
      }
      const tipoNome = this.getTipoVariavelNome(dado.tipo_variavel_id);
      dadosAgrupados[key].valores[`Dados_Variavel_${tipoNome}`] = dado.valor;
    });

    // Adicionar horários esperados que não têm dados
    horariosEsperados.forEach((tipos, key) => {
      if (!dadosAgrupados[key]) {
        dadosAgrupados[key] = {
          data_hora: key,
          valores: {}
        };
      }
    });

    // Converter para array e ordenar, mantendo a ordem das colunas
    const dadosExport = Object.values(dadosAgrupados)
      .map((item: any) => {
        const linha: any = {};

        // Criar linha seguindo a ordem definida
        ordemColunas.forEach(coluna => {
          if (coluna === 'Data/Hora') {
            linha[coluna] = moment(item.data_hora).format('DD/MM/YYYY HH:mm:ss');
          } else {
            const valor = item.valores[coluna];
            // Verificar se é coluna de Retrive ou Dados_Variavel
            if (coluna.startsWith('Retrive_')) {
              // Se não houver valor no retrive, exibir "sem retrive"
              if (valor === null || valor === undefined || valor === '') {
                linha[coluna] = 'sem retrive';
              } else {
                linha[coluna] = valor;
              }
            } else if (coluna.startsWith('Dados_Variavel_')) {
              // Se não houver valor nos dados da variável, exibir "sem coleta"
              if (valor === null || valor === undefined || valor === '') {
                linha[coluna] = 'sem coleta';
              } else {
                linha[coluna] = valor;
              }
            } else {
              linha[coluna] = valor || null;
            }
          }
        });

        return linha;
      })
      .sort((a: any, b: any) => {
        return moment(a['Data/Hora'], 'DD/MM/YYYY HH:mm:ss').diff(moment(b['Data/Hora'], 'DD/MM/YYYY HH:mm:ss'));
      });

    // Função para comparar valores (considerando números e strings)
    // Só retorna true se AMBOS os valores existirem e forem diferentes
    const valoresDiferentes = (valor1: any, valor2: any): boolean => {
      // Não destacar se algum valor for nulo, indefinido ou mensagem informativa
      if (valor1 === null || valor1 === undefined || valor2 === null || valor2 === undefined) {
        return false;
      }

      // Não destacar se algum valor for mensagem informativa
      const str1 = String(valor1).trim().toLowerCase();
      const str2 = String(valor2).trim().toLowerCase();
      if (str1 === 'sem retrive' || str1 === 'sem coleta' || str2 === 'sem retrive' || str2 === 'sem coleta') {
        return false;
      }

      // Converter para número se possível
      const num1 = typeof valor1 === 'number' ? valor1 : parseFloat(valor1);
      const num2 = typeof valor2 === 'number' ? valor2 : parseFloat(valor2);

      // Se ambos são números válidos, comparar numericamente
      if (!isNaN(num1) && !isNaN(num2)) {
        return Math.abs(num1 - num2) > 0.0001; // Tolerância para diferenças de ponto flutuante
      }

      // Caso contrário, comparar como string
      return str1 !== str2;
    };

    // Exportar para Excel - tentar usar exceljs primeiro (suporta estilos)
    import('exceljs').then((ExcelJSModule: any) => {
      if (ExcelJSModule && ExcelJSModule.Workbook) {
        // Usar ExcelJS (suporta estilos)
        const ExcelJS = ExcelJSModule;
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Comparativo');

        // Adicionar cabeçalhos
        const headerRow = worksheet.addRow(ordemColunas);
        
        // Estilizar cabeçalho
        headerRow.font = { bold: true };
        headerRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' } // Cinza claro
        };

        // Adicionar dados e aplicar estilos
        dadosExport.forEach((linha: any) => {
          const rowData: any[] = [];
          ordemColunas.forEach(coluna => {
            rowData.push(linha[coluna] ?? null);
          });
          
          const row = worksheet.addRow(rowData);
          
          // Verificar diferenças e aplicar cor apenas quando ambos valores existem e são diferentes
          tiposVariavelOrdenados.forEach((tipoId, tipoIndex) => {
            const tipoNome = this.getTipoVariavelNome(tipoId);
            const colDadosVariavel = `Dados_Variavel_${tipoNome}`;
            const colRetrive = `Retrive_${tipoNome}`;

            const colDadosIndex = ordemColunas.indexOf(colDadosVariavel);
            const colRetriveIndex = ordemColunas.indexOf(colRetrive);

            if (colDadosIndex >= 0 && colRetriveIndex >= 0) {
              const valorDados = linha[colDadosVariavel];
              const valorRetrive = linha[colRetrive];

              // Só pintar se ambos valores existem (não são null/undefined e não são mensagens informativas) e são diferentes
              if (valoresDiferentes(valorDados, valorRetrive)) {
                const cellDados = row.getCell(colDadosIndex + 1);
                const cellRetrive = row.getCell(colRetriveIndex + 1);
                
                if (cellDados && cellRetrive) {
                  cellDados.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFFFCCCC' } // Vermelho claro
                  };
                  
                  cellRetrive.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFFFCCCC' } // Vermelho claro
                  };
                }
              }
            }
          });
        });

        // Ajustar largura das colunas
        worksheet.columns.forEach((column: any, index: number) => {
          if (column) {
            if (index === 0) {
              column.width = 20; // Data/Hora
            } else {
              column.width = 15; // Colunas de valores
            }
          }
        });

        // Gerar buffer e salvar
        workbook.xlsx.writeBuffer().then((buffer: any) => {
          const nomeEstacao = estacao?.descricao || `Estacao_${estacao?.id || 'N/A'}`;
          this.saveAsExcelFile(buffer, `Comparativo_Periodo_${nomeEstacao}_${periodo.data_inicial}_${periodo.data_final}`);
        });
      } else {
        throw new Error('ExcelJS not available');
      }
    }).catch(() => {
      // Fallback para XLSX padrão (sem estilos avançados)
      import('xlsx').then(xlsx => {
        const worksheet = xlsx.utils.json_to_sheet(dadosExport, { header: ordemColunas });
        const workbook = { Sheets: { 'Comparativo': worksheet }, SheetNames: ['Comparativo'] };
        const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
        const nomeEstacao = estacao?.descricao || `Estacao_${estacao?.id || 'N/A'}`;
        this.saveAsExcelFile(excelBuffer, `Comparativo_Periodo_${nomeEstacao}_${periodo.data_inicial}_${periodo.data_final}`);
        
        this.messageService.add({
          severity: 'warn',
          summary: 'Aviso',
          detail: 'Para visualizar diferenças coloridas no Excel, instale: npm install exceljs'
        });
      });
    });
  }

}
